import React, { useEffect, useRef, useCallback } from 'react';
import { useHistory, withRouter } from 'react-router-dom';
import { useWindowSize } from './GraphicEditorHelpers';
import { useDesignHistory } from './services/HistoryService';
import { scaleText } from './services/ScaleService';
import { setMenuPosition } from './services/MenuService';
import { fontsService, getRuntimeFontFamily } from './services/FontsService';
import { refreshTransform } from './components/properties/PropertiesHelper';
import {
  attachTransformerToSingleNode,
  syncLayersStoreFromTransformer,
  syncTransformerToPropertiesSelection,
} from './utils/layerSelectionSync';

import useProductStore from './stores/useProductStore';
import usePropertiesStore from './stores/usePropertiesStore';
import useUiStore from './stores/useUiStore';

import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts';
import { useCanvasClip } from './hooks/useCanvasClip';
import { useStageSetup } from './hooks/useStageSetup';
import { useElementOperations } from './hooks/useElementOperations';
import { useAttachedElements } from './hooks/useAttachedElements';
import { useProductFeedEffects } from './hooks/useProductFeedEffects';
import { useGraphicEditorProductSync } from './hooks/useGraphicEditorProductSync';
import { useGraphicEditorBootstrap } from './hooks/useGraphicEditorBootstrap';
import { useDesignSave } from './hooks/useDesignSave';
import { useGraphicEditorMedia } from './hooks/useGraphicEditorMedia';
import { useEditorHistoryHandlers } from './hooks/useEditorHistory';
import {
  useEditorStageState,
  useEditorDesignState,
  useEditorProductState,
  useEditorUiState,
  useEditorPropertiesState,
} from './hooks/useGraphicEditorState';

import GraphicEditorLayout from './GraphicEditorLayout';
import { appBackendApi } from '../../app/appBackendApi';
import { parsePositiveCanvasDimension } from './utils/canvasDimensions';

const SIDEBAR_WIDTH = 284;
const MARGIN_X_OFFSET = 32;
const MARGIN_Y_OFFSET = 64;
const HEADER_TOP = 75;
const LAYER_DIFF = 320;
const MAX_DEPTH = 3;

const GraphicEditor = (props) => {
  const projectId = props.match.params.project_id;
  const designId = props.match.params.design_id === 'new' ? undefined : props.match.params.design_id;
  const urlHistory = useHistory();
  const [windowWidth, windowHeight] = useWindowSize();

  const {
    stage, setStage,
    clipOutline, setClipOutline,
    canvasSize, setCanvasSize,
    canvasPosition, setCanvasPosition,
    presetsDataSource, setPresetsDataSource,
    preset, setPreset,
    selectedElementPosition, setSelectedElementPosition,
  } = useEditorStageState();

  const {
    design, setDesign,
    designName, setDesignName,
    userImages, setUserImages,
    ruleId, setRuleId,
  } = useEditorDesignState();

  const {
    project, setProject,
    feedPage,
    feedPageIndex,
    productIndex,
    product, setProduct,
    setProductFields,
    reloadingProducts,
    toPrevProduct,
    toNextProduct,
  } = useEditorProductState();

  const {
    isLayersPanelOpen, isFontsPanelOpen,
    isProductDataPanelOpen, isProductPropertiesOpen,
    toggleLayersPanel, toggleFontsPanel,
    toggleProductDataPanel, toggleProductPropertiesPanel,
    setProductDataPanelOpen,
  } = useEditorUiState();

  const { selectedGroup, setSelectedGroup, refreshProperties } = useEditorPropertiesState();

  const [saveHistory, history, historyUndo, historyRedo] = useDesignHistory();
  const layerRef = useRef();
  const routeProjectIdNum = Number(projectId);

  useEffect(() => {
    useProductStore.getState().init(appBackendApi);
  }, []);

  useEffect(() => {
    usePropertiesStore.getState().initCallbacks({ saveHistory });
  }, [saveHistory]);

  const refreshLayersStructure = useCallback(() => {
    if (layerRef.current) layerRef.current.refresh();
  }, []);

  const setElementVisibility = useCallback((element, value) => {
    element.setVisible(value);
    element.setAttr('invisible', !value);
  }, []);

  const attachedOps = useAttachedElements();
  const attachedOpsRef = useRef(attachedOps);
  attachedOpsRef.current = attachedOps;

  const elementOps = useElementOperations({
    stage,
    canvasPosition,
    product,
    api: appBackendApi,
    layerDiff: LAYER_DIFF,
    maxDepth: MAX_DEPTH,
    selectedGroup,
    setSelectedGroup,
    setSelectedElementPosition,
    refreshProperties,
    refreshLayersStructure,
    saveHistory,
    moveAttachedSources: attachedOps.moveAttachedSources,
    updateSourceAttachedOffset: attachedOps.updateSourceAttachedOffset,
    detachElement: attachedOps.detachElement,
    destroyDistributedGroup: attachedOps.destroyDistributedGroup,
  });

  const stageOps = useStageSetup({
    canvasSize,
    canvasPosition,
    presetsDataSource,
    sidebarWidth: SIDEBAR_WIDTH,
    layerDiff: LAYER_DIFF,
    maxDepth: MAX_DEPTH,
    fonts: appBackendApi.fonts,
    setStage,
    setCanvasSize,
    setCanvasPosition,
    setPresetsDataSource,
    setPreset,
    setDesignName,
    setSelectedGroup,
    setSelectedElementPosition,
    refreshProperties,
    refreshLayersStructure,
    handleTransformEnd: elementOps.handleTransformEnd,
    moveAttachedSources: attachedOps.moveAttachedSources,
    updateSourceAttachedOffset: attachedOps.updateSourceAttachedOffset,
    saveHistory,
    toggleProductPropertiesPanel,
  });

  const { initProject, getImages } = useGraphicEditorBootstrap({
    api: appBackendApi,
    projectId,
    designId,
    setProject,
    setRuleId,
    setDesign,
    setUserImages,
    setStage,
    createStage: () => stageOps.createStage(),
  });

  const { saveDesign } = useDesignSave({
    stage,
    designName,
    designId,
    projectId,
    canvasSize,
    userImages,
    fontsLibrary: appBackendApi.fonts,
    initProject,
    history: props.history,
  });

  useGraphicEditorProductSync({
    product,
    stage,
    project,
    routeProjectIdNum,
    setElementVisibility,
    attachedOpsRef,
  });

  const { afterHistoryChangedAction, handleHistoryUndo, handleHistoryRedo } =
    useEditorHistoryHandlers({
      stage,
      deleteElement: elementOps.deleteElement,
      historyUndo,
      historyRedo,
      refreshLayersStructure,
      setSelectedGroup,
    });

  useKeyboardShortcuts({
    selectedGroup,
    stage,
    copyElement: elementOps.copyElement,
    deleteElement: elementOps.deleteElement,
    createGroup: elementOps.createGroup,
    destroyGroup: elementOps.destroyGroup,
    historyUndoAction: () => historyUndo(stage, elementOps.deleteElement, afterHistoryChangedAction),
    historyRedoAction: () => historyRedo(stage, elementOps.deleteElement, afterHistoryChangedAction),
  });

  useCanvasClip({ stage, clipOutline, canvasPosition, canvasSize });

  useEffect(() => {
    if (!stage) return;
    try {
      stage.width(windowWidth - SIDEBAR_WIDTH - MARGIN_X_OFFSET);
      stage.height(windowHeight - MARGIN_Y_OFFSET);
    } catch (err) {
      console.warn('[GraphicEditor] stage resize failed, likely broken image in scene', err);
    }
  }, [stage, windowWidth, windowHeight]);

  useEffect(() => {
    if (design) {
      setDesignName(design.name);
      setStage(stageOps.loadStage(design));
      setSelectedGroup(null);
    }
  }, [design]);

  useEffect(() => {
    const menuNode = document.getElementById('menu');
    if (selectedGroup) {
      stage?.findOne('#selectionRectangle')?.moveToTop();
      const tr = stage?.findOne('Transformer');
      tr?.moveToTop();
      refreshProperties();
      let selectedBackground = selectedGroup.findOne('.background');
      if (!selectedGroup.attrs.name) selectedBackground = selectedGroup.findOne('.back');
      setSelectedElementPosition({ x: selectedGroup.x(), y: selectedGroup.y() });
      setMenuPosition(stage, selectedGroup, selectedBackground, LAYER_DIFF);
    } else if (menuNode) {
      menuNode.style.display = 'none';
    }
    refreshLayersStructure();
    if (selectedGroup && useUiStore.getState().isProductDataPanelOpen) {
      setProductDataPanelOpen(false);
    }
  }, [selectedGroup]);

  useEffect(() => {
    if (!stage) return;
    syncTransformerToPropertiesSelection(stage, selectedGroup);
    syncLayersStoreFromTransformer(stage);
  }, [stage, selectedGroup]);

  useEffect(() => {
    if (selectedGroup) {
      let selectedBackground = selectedGroup.findOne('.background');
      if (!selectedGroup.attrs.name) selectedBackground = selectedGroup.findOne('.back');
      setMenuPosition(stage, selectedGroup, selectedBackground, LAYER_DIFF);
      refreshLayersStructure();
    }
  }, [selectedGroup, selectedElementPosition]);

  useProductFeedEffects({
    project,
    feedPage,
    feedPageIndex,
    productIndex,
    reloadingProducts,
    setProduct,
    setProductFields,
  });

  useEffect(() => {
    if (!stage) return;
    const captionLayer = stage.findOne('#captionLayer');
    if (captionLayer) {
      const text = captionLayer.findOne('Text');
      if (text) {
        text.text(getDesignViewName(designName, preset, canvasSize.width, canvasSize.height));
        captionLayer.batchDraw();
      }
    }
  }, [preset, designName, stage, canvasSize]);

  useEffect(() => {
    if (!stage || !preset) return;
    const presetItem = presetsDataSource.find((t) => t.id === preset);
    if (presetItem) stage.setAttr('preset_name', presetItem.name);
  }, [preset]);

  useEffect(() => {
    if (!stage) return;
    const w = parsePositiveCanvasDimension(canvasSize.width);
    const h = parsePositiveCanvasDimension(canvasSize.height);
    if (w != null && h != null) {
      stage.setAttr('canvas_width', w);
      stage.setAttr('canvas_height', h);
    }
  }, [stage, canvasSize]);

  useEffect(() => {
    if (appBackendApi?.fonts?.length > 0) fontsService.loadFonts(appBackendApi.fonts);
  }, [appBackendApi.fonts]);

  useEffect(() => {
    if (stage) saveHistory(stage);
  }, [stage]);

  const { uploadImage, deleteImage, uploadFont, deleteFont } = useGraphicEditorMedia({
    project,
    getImages,
  });

  const scaleTextWithAttachedUpdate = useCallback(
    async (text, res, rej) => {
      await scaleText(text, res, rej);
      const group = text?.parent;
      refreshTransform(group);
      if (group?.getAttr('attachIds')?.length) await attachedOps.moveAttachedSources(group);
    },
    [attachedOps],
  );

  const exit = useCallback(
    () => urlHistory.push({ pathname: `/project/${projectId}/rule/${ruleId}`, search: window.location.search }),
    [urlHistory, projectId, ruleId],
  );

  const selectFromLayout = useCallback(
    (elements) => {
      if (!stage) return;
      const tr = stage.findOne('Transformer');
      if (!tr) return;
      if (!elements?.length) {
        tr.nodes([]);
        setSelectedGroup(null);
        return;
      }
      if (elements.length === 1) {
        attachTransformerToSingleNode(tr, elements[0]);
        setSelectedGroup(elements[0]);
      } else {
        tr.nodes(elements);
        setSelectedGroup(tr);
      }
    },
    [stage, setSelectedGroup],
  );

  const addTextWithFont = useCallback(
    (font) => {
      if (font) elementOps.addText(undefined, getRuntimeFontFamily(font));
    },
    [elementOps],
  );

  const getDesignViewName = (name, presetId, width, height) => {
    const actualPreset = presetsDataSource.find((t) => t.id === presetId);
    let actualPresetName = '';
    if (actualPreset) actualPresetName = !actualPreset.customSize ? actualPreset.name : `${actualPreset.name} ${width}x${height} px`;
    return name + (actualPresetName ? ` (${actualPresetName})` : '');
  };

  const onDesignPropertiesChanged = useCallback(
    (name, presetId, width, height) => {
      setDesignName(name);
      setPreset(presetId);
      const w = parsePositiveCanvasDimension(width);
      const h = parsePositiveCanvasDimension(height);
      if (w != null && h != null) {
        setCanvasSize({ width: w, height: h });
      }
    },
    [setDesignName, setPreset, setCanvasSize],
  );

  const closeOpenProductData = useCallback(() => {
    const currentState = useUiStore.getState().isProductDataPanelOpen;
    if (!currentState) {
      void useProductStore.getState().resetFeedSearch();
    }
    toggleProductDataPanel();
    if (currentState) selectFromLayout([]);
  }, [toggleProductDataPanel, selectFromLayout]);

  const onDraggingElementFinish = useCallback(
    async (e, type, value = null) => {
      e.preventDefault();
      await stage.setPointersPositions(e);
      const containerRect = stage.container().getBoundingClientRect();
      const positions = {
        x: stage.getRelativePointerPosition().x - containerRect.x + LAYER_DIFF,
        y: stage.getRelativePointerPosition().y - containerRect.y + HEADER_TOP,
      };
      if (type === 'text') return elementOps.addText(value, null, positions);
      if (type === 'image') return elementOps.addImage(value, positions);
    },
    [stage, elementOps],
  );

  const handleCanvasOnOff = useCallback(() => {
    setClipOutline(!clipOutline);
  }, [clipOutline, setClipOutline]);

  const handleCloseProperties = useCallback(() => {
    setSelectedGroup(null);
  }, [setSelectedGroup]);

  const handleZoomIn = useCallback(() => stageOps.zoomStage(stage, 1), [stageOps, stage]);
  const handleZoomOut = useCallback(() => stageOps.zoomStage(stage, -1), [stageOps, stage]);
  const handleDragOver = useCallback((e) => e.preventDefault(), []);

  return (
    <GraphicEditorLayout
      ref={layerRef}
      sidebarWidth={SIDEBAR_WIDTH}
      stage={stage}
      selectedGroup={selectedGroup}
      designName={designName}
      refreshProperties={refreshProperties}
      setDesignName={setDesignName}
      isProductDataPanelOpen={isProductDataPanelOpen}
      isLayersPanelOpen={isLayersPanelOpen}
      isFontsPanelOpen={isFontsPanelOpen}
      isProductPropertiesOpen={isProductPropertiesOpen}
      toggleLayersPanel={toggleLayersPanel}
      toggleFontsPanel={toggleFontsPanel}
      toggleProductPropertiesPanel={toggleProductPropertiesPanel}
      elementOps={elementOps}
      saveHistory={saveHistory}
      history={history}
      handleHistoryUndo={handleHistoryUndo}
      handleHistoryRedo={handleHistoryRedo}
      exit={exit}
      saveDesign={saveDesign}
      handleCanvasOnOff={handleCanvasOnOff}
      closeOpenProductData={closeOpenProductData}
      onDraggingElementFinish={onDraggingElementFinish}
      scaleTextWithAttachedUpdate={scaleTextWithAttachedUpdate}
      handleCloseProperties={handleCloseProperties}
      uploadImage={uploadImage}
      deleteImage={deleteImage}
      uploadFont={uploadFont}
      deleteFont={deleteFont}
      selectFromLayout={selectFromLayout}
      addTextWithFont={addTextWithFont}
      onDesignPropertiesChanged={onDesignPropertiesChanged}
      handleDragOver={handleDragOver}
      handleZoomIn={handleZoomIn}
      handleZoomOut={handleZoomOut}
      toPrevProduct={toPrevProduct}
      toNextProduct={toNextProduct}
    />
  );
};

export default withRouter(GraphicEditor);
